/**
 * Build GameStateView from domain session or app state (for RenderPort.update).
 * Plan 05: mapStateToView for scene "map".
 */

import type { GemType, SceneId } from "../domain/types.js";
import type { GameSession } from "../domain/session.js";
import { effectiveTowerCost, effectiveWallCost, effectiveAmplifierCost, effectiveGemCreateCost, effectiveTrapCost, effectiveShrineCost, waveTotal, waveSpawned, TRAP_DAMAGE_MULT, TRAP_EFFECT_MULT, gemUpgradeCost, MAX_GEM_LEVEL } from "../domain/session.js";
import { GEM_SPECS } from "../domain/gem-specs.js";
import { getTowerRadius, getFireRateMs } from "../domain/tower-shooting.js";
import {
  BLUE_ARMOR_PIERCE_PER_LEVEL,
  CYAN_SHOCK_CHANCE,
  CYAN_SHOCK_MS,
  GREEN_POISON_DPS_PER_LEVEL,
  GREEN_POISON_MS,
  LIME_CHAIN_CHANCE,
  LIME_CHAIN_DMG_MULT,
  ORANGE_SPLASH_CHANCE,
  ORANGE_SPLASH_RADIUS,
  PURPLE_SLOW_MS,
  PURPLE_SLOW_RATIO,
  RED_CRIT_CHANCE,
  RED_CRIT_MULT,
  YELLOW_MANA_PER_LEVEL,
  effectChanceForLevel,
  effectDurationForLevel,
  effectMagnitudeForLevel,
} from "../domain/gem-effects.js";
import { stageBaseXp } from "../domain/mage-xp.js";
import { getXpMultiplier } from "../domain/stage-settings.js";
import type { GameStateView, MageView, SkillView, NextWavePreview, MageStatsView, CampaignUiView } from "./ports.js";
import { MONSTER_SPECS } from "../domain/monster-specs.js";
import type { AppState } from "./types.js";
import { activeCampaign, type CampaignConfig } from "../campaign/index.js";
import type { MageProfile } from "../domain/mage-profile.js";
import { availableSkillPoints } from "../domain/mage-profile.js";
import { ACHIEVEMENTS } from "../domain/achievements.js";
import { levelFromXp } from "../domain/mage-xp.js";
import { playerLevelReq } from "../domain/mage-xp.js";
import {
  SKILL_SPECS,
  canLearn,
  getSkillEffectiveValue,
  getSkillCost,
  getSkillPlayerLevelReq,
} from "../domain/skills.js";
import { t } from "../i18n/index.js";

// Re-export for consumers who only import state-view
export type { MageStatsView };

function campaignUiToView(campaign: CampaignConfig = activeCampaign): CampaignUiView {
  const ui = campaign.ui;
  return {
    brandTitle: ui.brandTitle,
    brandIcon: ui.brandIcon,
    mageIcon: ui.mageIcon,
    mageLabelKey: ui.mageLabelKey,
    resourceLabelKey: ui.resourceLabelKey,
    waveTypeCodes: { ...ui.waveTypeCodes },
  };
}

function percent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function ms(value: number): string {
  return `${Math.round(value)}ms`;
}

function buildEffectDetails(gemType: GemType, level: number, trapEffectMult = 1): string[] {
  const magnitude = effectMagnitudeForLevel(level) * trapEffectMult;
  const duration = effectDurationForLevel(level) * trapEffectMult;
  switch (gemType) {
    case "Yellow":
      return [t("gem.effectDetail.Yellow", { mana: (YELLOW_MANA_PER_LEVEL * level * magnitude).toFixed(2) })];
    case "Orange":
      return [t("gem.effectDetail.Orange", { chance: percent(effectChanceForLevel(ORANGE_SPLASH_CHANCE, level) * trapEffectMult), radius: ORANGE_SPLASH_RADIUS })];
    case "Red":
      return [t("gem.effectDetail.Red", { chance: percent(effectChanceForLevel(RED_CRIT_CHANCE, level)), mult: RED_CRIT_MULT })];
    case "Purple":
      return [t("gem.effectDetail.Purple", { slow: percent(PURPLE_SLOW_RATIO * magnitude), duration: ms(PURPLE_SLOW_MS * duration) })];
    case "Green":
      return [t("gem.effectDetail.Green", { dps: (GREEN_POISON_DPS_PER_LEVEL * level * magnitude).toFixed(1), duration: ms(GREEN_POISON_MS * duration) })];
    case "Blue":
      return [t("gem.effectDetail.Blue", { armor: (BLUE_ARMOR_PIERCE_PER_LEVEL * level * magnitude).toFixed(2) })];
    case "Cyan":
      return [t("gem.effectDetail.Cyan", { chance: percent(effectChanceForLevel(CYAN_SHOCK_CHANCE, level) * trapEffectMult), duration: ms(CYAN_SHOCK_MS * duration) })];
    case "Lime":
      return [t("gem.effectDetail.Lime", { chance: percent(effectChanceForLevel(LIME_CHAIN_CHANCE, level) * trapEffectMult), damage: percent(LIME_CHAIN_DMG_MULT * magnitude) })];
  }
}

/** Convert MageProfile to MageStatsView DTO. */
export function statsToView(profile: MageProfile): MageStatsView {
  // Guard against legacy profiles without stats (migration safety)
  const stats = profile.stats ?? {
    totalKills: 0,
    totalBanishes: 0,
    totalTimePlayed: 0,
    stageVictories: {} as Record<string, number>,
    stageFailures: {} as Record<string, number>,
    totalBattles: 0,
    manaFromEarlyWaveSpawns: 0,
    earlyWaveHurries: 0,
  };
  const totalVictories = Object.values(stats.stageVictories).reduce((a, b) => a + b, 0);
  const totalFailures = Object.values(stats.stageFailures).reduce((a, b) => a + b, 0);
  const fieldsCompleted = Object.keys(stats.stageVictories).length;
  const totalGames = totalVictories + totalFailures;
  const winRate = totalGames > 0 ? Math.round((totalVictories / totalGames) * 100) + "%" : "—";
  const timeSec = Math.floor(stats.totalTimePlayed / 1000);
  const hours = Math.floor(timeSec / 3600);
  const mins = Math.floor((timeSec % 3600) / 60);
  const timeFormatted = hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  const fieldIds = new Set([
    ...Object.keys(stats.stageVictories),
    ...Object.keys(stats.stageFailures),
    ...Object.keys(profile.stageHighestXps),
  ]);
  const perField = [...fieldIds].map((fid) => ({
    fieldId: fid,
    wins: stats.stageVictories[fid] ?? 0,
    fails: stats.stageFailures[fid] ?? 0,
    highestXp: profile.stageHighestXps[fid] ?? 0,
  })).sort((a, b) => b.highestXp - a.highestXp).slice(0, 10);
  const unlockedSet = new Set(profile.unlockedAchievements ?? []);
  const achievements = ACHIEVEMENTS.map((a) => ({
    id: a.id,
    name: a.name,
    description: a.description,
    unlocked: unlockedSet.has(a.id),
  }));

  return {
    totalKills: stats.totalKills,
    totalBanishes: stats.totalBanishes,
    totalTimePlayedFormatted: timeFormatted,
    totalBattles: stats.totalBattles,
    totalVictories,
    totalFailures,
    winRate,
    fieldsCompleted,
    manaFromEarlyWaveSpawns: stats.manaFromEarlyWaveSpawns ?? 0,
    earlyWaveHurries: stats.earlyWaveHurries ?? 0,
    perField,
    achievements,
  };
}

/** Build SkillView[] from MageProfile. */
export function skillsToView(
  profile: MageProfile,
  campaign: CampaignConfig = activeCampaign
): { skills: SkillView[]; pointsLeft: number; pointsTotal: number } {
  // Guard against legacy profiles without skillLevels (migration safety)
  const safeProfile = profile.skillLevels == null
    ? { ...profile, skillLevels: {} as Record<number, number> }
    : profile;
  const mageLevel = levelFromXp(safeProfile.totalXp);
  const pointsTotal = mageLevel;
  const points = availableSkillPoints(safeProfile, mageLevel);
  const hiddenSkillIds = new Set<number>(campaign.skills.hiddenSkillIds);
  const skills: SkillView[] = SKILL_SPECS.filter((spec) => !hiddenSkillIds.has(spec.id)).map((spec) => {
    const level = safeProfile.skillLevels[spec.id] ?? 0;
    const maxed = level >= spec.maxLevel;
    const effectiveValue = getSkillEffectiveValue(spec.id, level);
    const nextLevel = level + 1;
    const nextLevelCost = maxed ? 0 : getSkillCost(spec.id, nextLevel);
    const reqLevel = getSkillPlayerLevelReq(spec.id);
    const able = canLearn(safeProfile.skillLevels, spec.id, mageLevel, points);

    let lockReason: SkillView["lockReason"] = null;
    if (!able) {
      if (maxed) {
        lockReason = "maxed";
      } else if (mageLevel < reqLevel) {
        lockReason = "needs-level";
      } else {
        lockReason = "needs-points";
      }
    }

    return {
      id: spec.id,
      name: spec.name,
      nameKey: `${campaign.skills.nameKeyPrefix}.${spec.id}`,
      descriptionKey: `${campaign.skills.descriptionKeyPrefix}.${spec.id}`,
      level,
      maxLevel: spec.maxLevel,
      effectiveValue,
      nextLevelCost,
      canLearn: able,
      canRefund: level > 0,
      lockReason,
      nextLevelReq: reqLevel,
    };
  });
  return { skills, pointsLeft: points, pointsTotal };
}

/** Convert MageProfile to MageView DTO. */
export function mageToView(profile: MageProfile): MageView {
  const level = levelFromXp(profile.totalXp);
  const xpForCurrentLevel = playerLevelReq(level);
  const xpForNextLevel = playerLevelReq(level + 1);
  return {
    level,
    totalXp: profile.totalXp,
    xpForCurrentLevel,
    xpForNextLevel,
    xpToNext: xpForNextLevel - profile.totalXp,
  };
}

/** Build view for scene "map" (LoadProgress, GoToMap). */
export function mapStateToView(
  state: AppState,
  mageProfile?: MageProfile,
  skillsPanelOpen?: boolean,
  statsPanelOpen?: boolean,
  campaign: CampaignConfig = activeCampaign
): GameStateView {
  const mp = state.mapProgress;
  const skillData = mageProfile ? skillsToView(mageProfile, campaign) : null;
  const statsData = mageProfile ? statsToView(mageProfile) : undefined;
  return {
    scene: "map",
    campaignUi: campaignUiToView(campaign),
    map: {
      fieldIds: [...campaign.map.fieldIds],
      rows: campaign.map.rows,
      cols: campaign.map.cols,
      startingFieldId: campaign.map.startingFieldId,
      playableFieldIds: campaign.map.fieldIds.filter((fieldId) => campaign.map.isFieldPlayable(fieldId)),
      selectedFieldId: state.selectedFieldId ?? null,
      mapProgress: mp
        ? {
            unlockedFieldIds: [...mp.unlockedFieldIds],
            completedFieldIds: [...mp.completedFieldIds],
          }
        : null,
      mage: mageProfile ? mageToView(mageProfile) : null,
      skills: skillData?.skills,
      pointsLeft: skillData?.pointsLeft,
      pointsTotal: skillData?.pointsTotal,
      skillsPanelOpen: skillsPanelOpen ?? false,
      statsPanelOpen: statsPanelOpen ?? false,
      stats: statsData,
    },
  };
}

/** Build view for scene "story". */
export function storyToView(
  state: AppState,
  campaign: CampaignConfig = activeCampaign
): GameStateView {
  if (!state.story) {
    return { scene: "story", campaignUi: campaignUiToView(campaign) };
  }
  const beatId = state.story.beatId;
  const panelIdx = state.story.panelIndex;
  const panels = (campaign.story.beats[beatId] ?? []).map((panel) => ({
    imageSrc: panel.imageSrc,
    text: t(panel.textKey),
  }));

  return {
    scene: "story",
    campaignUi: campaignUiToView(campaign),
    story: {
      beatId,
      panels,
      currentPanelIndex: panelIdx,
    },
  };
}


export interface DragState {
  gem: { id: string; type: string; level: number; secondaryType?: string };
  screenX: number;
  screenY: number;
}

export function sessionToView(
  session: GameSession,
  scene: SceneId,
  dragState?: DragState | null,
  placementMode?: "Tower" | "Wall" | "Amplifier" | "Trap" | "Shrine",
  tutorialStep?: number,
  campaign: CampaignConfig = activeCampaign
): GameStateView {
  // Phase D: compose waypoints as [...entryCells, exitCell] for render (rift on [0], altar on last).
  const waypoints = session.field
    ? (session.field.entryCells && session.field.exitCell
        ? [...session.field.entryCells, session.field.exitCell]
        : (session.field.waypoints ?? []))
    : [];
  const monsters =
    scene === "game" && session.monsters?.length
      ? session.monsters.map((m) => ({
          id: m.id,
          hp: m.hp,
          maxHp: m.maxHp,
          armor: m.armor,
          row: m.position.row,
          col: m.position.col,
          type: m.type ?? "Normal",
          slowed: (m.slowUntilMs ?? 0) > session.elapsedMs,
          poisoned: (m.poisonUntilMs ?? 0) > session.elapsedMs,
          shocked: (m.shockUntilMs ?? 0) > session.elapsedMs,
          dirIndex: m.dirIndex,
        }))
      : undefined;
  const shots =
    scene === "game" && session.shots?.length
      ? session.shots.map((s) => ({
          id: s.id,
          row: s.pos.row,
          col: s.pos.col,
          gemType: s.gemType,
          secondaryGemType: s.secondaryGemType,
        }))
      : undefined;
  const hitFlashes =
    scene === "game" && session.hitFlashes?.length
      ? session.hitFlashes.map((f) => ({
          id: f.id,
          row: f.row,
          col: f.col,
          ageMs: session.elapsedMs - f.startMs,
          durationMs: f.durationMs,
          gemType: f.gemType,
        }))
      : undefined;
  const currentWave =
    scene === "game" &&
    session.waves?.length &&
    session.currentWaveIndex < session.waves.length
      ? (() => {
          const w = session.waves[session.currentWaveIndex];
          return { index: w.index, totalMonsters: waveTotal(w), spawned: waveSpawned(w) };
        })()
      : undefined;

  let nextWave: NextWavePreview | null | undefined;
  let wavePreviews: NextWavePreview[] | undefined;
  if (scene === "game" && session.waves?.length) {
    wavePreviews = session.waves.map((w, idx) => {
      const type = w.monsterType as "Normal" | "Swarm" | "Armored" | "Giant" | "Runner";
      const spec = MONSTER_SPECS[type];
      const hpBase = session.monsterBaseHp * Math.pow(session.hpGrowthRate, idx);
      const bossMult = w.isBoss ? 2 : 1;
      return {
        type,
        approxHp: Math.round(hpBase * spec.hpMult * bossMult),
        monsterCount: waveTotal(w),
        isBoss: w.isBoss,
      };
    });
    const nextIdx = session.currentWaveIndex + 1;
    if (nextIdx >= session.waves.length) {
      nextWave = null;
    } else {
      nextWave = wavePreviews[nextIdx];
    }
  }

  // Battle summary: populate when battle has ended
  let battleSummary: GameStateView["battleSummary"] | undefined;
  if (session.won !== null) {
    const stats = session.sessionStats;
    const earnedXp = session.won
      ? Math.round(
          stageBaseXp(session.originalWavesNum, session.originalMonsterBaseHp) *
          getXpMultiplier(session.stageSettings)
        )
      : null;
    battleSummary = {
      won: session.won,
      killsThisBattle: stats.killsThisBattle,
      banishesThisBattle: stats.banishesThisBattle,
      bombsCastThisBattle: stats.bombsCastThisBattle,
      earlyHurryCount: stats.earlyHurryCount,
      manaFromEarlyHurry: stats.manaFromEarlyHurry,
      durationMs: session.elapsedMs,
      earnedXp,
    };
  }

  return {
    scene,
    campaignUi: campaignUiToView(campaign),
    field: session.field
      ? {
          id: session.field.id,
          rows: session.field.rows,
          cols: session.field.cols,
        }
      : undefined,
    mana: { current: session.mana.current, max: session.mana.max },
    towerCost: effectiveTowerCost(session),
    wallCost: effectiveWallCost(session),
    amplifierCost: effectiveAmplifierCost(session),
    trapCost: effectiveTrapCost(session),
    shrineCost: effectiveShrineCost(session),
    placementMode: placementMode ?? "Tower",
    towers: session.towers.map((tower) => {
      let stats = undefined;
      const slot = tower.gemSlots.find((s) => s.gem !== null);
      if (slot?.gem) {
        const gemType = slot.gem.type as GemType;
        const spec = GEM_SPECS[gemType];
        if (spec) {
          const dmgMult = slot.gem.mods?.dmgMult ?? 1;
          const skillDmg = 1 + 0.01 * session.skillEffects.damage;
          const trapMult = tower.type === "Trap" ? TRAP_DAMAGE_MULT * (1 + 0.01 * session.skillEffects.traps) : 1;
          const trapEffectMult = tower.type === "Trap" ? TRAP_EFFECT_MULT * (1 + 0.01 * session.skillEffects.traps) : 1;
          const baseMultiplier = slot.gem.level * skillDmg * trapMult * dmgMult;
          stats = {
            range: getTowerRadius(gemType, session, tower, slot.gem.level),
            damageMin: Math.round(spec.damageMin * baseMultiplier),
            damageMax: Math.round(spec.damageMax * baseMultiplier),
            fireRateMs: getFireRateMs(session, tower),
            specialEffect: t(`gem.effect.${slot.gem.type}`),
            effectDetails: buildEffectDetails(gemType, slot.gem.level, trapEffectMult),
            upgradeCost: slot.gem.level < MAX_GEM_LEVEL ? gemUpgradeCost(slot.gem.level) : undefined,
          };
        }
      }
      return {
        id: tower.id,
        cell: { ...tower.cell },
        type: tower.type,
        gemSlots: tower.gemSlots.map((s) => ({
          gem: s.gem ? { type: s.gem.type, level: s.gem.level, secondaryType: s.gem.secondaryType } : null,
        })),
        lastDischargeAt: tower.lastDischargeAt,
        stats,
      };
    }),
    elapsedMs: session.elapsedMs,
    waypoints: waypoints.length
      ? waypoints.map((w) => ({ row: w.row, col: w.col }))
      : undefined,
    pathCells: session.pathCells?.length
      ? session.pathCells.map((c) => ({ row: c.row, col: c.col }))
      : undefined,
    originalPathCells: session.field?.walkableCells?.length
      ? session.field.walkableCells.map((c) => ({ row: c.row, col: c.col }))
      : undefined,
    blockedCells: session.field?.blockedCells?.length
      ? session.field.blockedCells.map((c) => ({ row: c.row, col: c.col }))
      : undefined,
    monsters,
    shots,
    hitFlashes,
    currentWave,
    totalWaves: session.waves?.length ?? undefined,
    gemInventory: session.gemInventory?.length
      ? session.gemInventory.map((g) => {
          const spec = GEM_SPECS[g.type as keyof typeof GEM_SPECS];
          let stats: NonNullable<NonNullable<GameStateView["gemInventory"]>[number]["stats"]> | undefined;
          if (spec) {
            const skillDmg = 1 + 0.01 * session.skillEffects.damage;
            const dmgMult = g.mods?.dmgMult ?? 1;
            const baseMult = g.level * skillDmg * dmgMult;
            let rate = spec.fireRateMs / (1 + 0.01 * session.skillEffects.firingSpeed);
            if (g.level > 1) rate /= (1 + (g.level - 1) * 0.05);
            rate *= g.mods?.fireRateMult ?? 1;
            const range = spec.rangeCells * (1 + 0.01 * (session.skillEffects.range ?? 0));
            stats = {
              range,
              damageMin: Math.round(spec.damageMin * baseMult),
              damageMax: Math.round(spec.damageMax * baseMult),
              fireRateMs: Math.round(rate),
              specialEffect: t(`gem.effect.${g.type}`),
              effectDetails: buildEffectDetails(g.type as GemType, g.level),
              upgradeCost: g.level < MAX_GEM_LEVEL ? gemUpgradeCost(g.level) : undefined,
            };
          }
          return { id: g.id, type: g.type, level: g.level, secondaryType: g.secondaryType, stats };
        })
      : undefined,
    currentWaveIndex: session.currentWaveIndex,
    speed: session.speed,
    won: session.won,
    gemCreateCost: effectiveGemCreateCost(session),
    allowedGemTypes: session.field.allowedGemTypes ? [...session.field.allowedGemTypes] : undefined,
    dragState: dragState
      ? {
          gem: { type: dragState.gem.type, level: dragState.gem.level, secondaryType: dragState.gem.secondaryType },
          screenX: dragState.screenX,
          screenY: dragState.screenY,
        }
      : undefined,
    nextWave,
    wavePreviews,
    battleSummary,
    tutorialStep: tutorialStep ?? -1,
  };
}
